import { useState } from "react";
import { Ruler, X, ChevronDown } from "lucide-react";

// Which measurement hints to show, based on the chart being viewed.
function measurementHintsFor(chartKey) {
  const all = {
    chest: {
      label: "Chest",
      tip: "Wrap the tape around the fullest part of the chest, under the armpits. Keep the tape level and snug — not tight.",
    },
    waist: {
      label: "Waist",
      tip: "Wrap the tape around the narrowest part of the natural waistline, just above the belly button.",
    },
    hip: {
      label: "Hip",
      tip: "Feet together. Measure around the widest part of the hips and bum, about 20 cm below the natural waist.",
    },
    head: {
      label: "Head",
      tip: "Wrap the tape around the head 2 cm above the eyebrows and ears — the widest part of the skull.",
    },
    calf: {
      label: "Calf",
      tip: "Measure around the widest part of the calf muscle, standing with feet flat on the floor.",
    },
    inseam: {
      label: "Body Length",
      tip: "From the top of the shoulder near the collar, straight down to the desired garment hem.",
    },
  };
  const map = {
    "scrum-caps": ["head"],
    "shoulder-pads": ["chest"],
    "shin-skins": ["calf"],
    "shorts-boys-men": ["waist", "hip"],
    "rugby-shorts": ["waist", "hip"],
    "rugby-shirts": ["chest", "inseam"],
    "dress-girls-women": ["chest", "waist", "hip"],
  };
  return (map[chartKey] || ["chest", "waist", "hip"]).map((k) => all[k]);
}

// Compact SVG silhouette with measurement guide lines. Highlights only the
// dimensions relevant to the current chart.
function HowToMeasureDiagram({ points }) {
  const has = (k) => points.some((p) => p.label.toLowerCase().startsWith(k));
  const showHead = has("head");
  const showChest = has("chest");
  const showWaist = has("waist") || has("body");
  const showHip = has("hip");
  const showCalf = has("calf");
  return (
    <svg
      viewBox="0 0 220 380"
      className="w-40 h-auto flex-shrink-0"
      aria-hidden="true"
    >
      {/* silhouette */}
      <path
        d="M110 22 c-14 0 -22 12 -22 25 c0 10 4 18 11 22 c-5 4 -9 8 -12 12
           c-14 4 -30 12 -32 24 l-3 22 c-1 8 6 12 12 10 l0 42 c0 8 4 16 8 22
           l3 6 c-2 12 -5 30 -7 46 c-1 12 2 22 6 40 c1 10 -2 22 -2 32 c0 6 6 8 10 8
           c8 0 12 -6 12 -14 c0 -14 -2 -30 -3 -40 c-1 -14 1 -34 4 -50 c1 -8 3 -14 3 -14
           l6 0 c0 0 2 6 3 14 c3 16 5 36 4 50 c-1 10 -3 26 -3 40 c0 8 4 14 12 14
           c4 0 10 -2 10 -8 c0 -10 -3 -22 -2 -32 c4 -18 7 -28 6 -40 c-2 -16 -5 -34 -7 -46
           l3 -6 c4 -6 8 -14 8 -22 l0 -42 c6 2 13 -2 12 -10 l-3 -22 c-2 -12 -18 -20 -32 -24
           c-3 -4 -7 -8 -12 -12 c7 -4 11 -12 11 -22 c0 -13 -8 -25 -22 -25 z"
        fill="#1a1a1a"
        stroke="#525252"
        strokeWidth="1.2"
      />
      {/* HEAD */}
      {showHead && (
        <g>
          <ellipse cx="110" cy="35" rx="26" ry="16" fill="none" stroke="#F7931E" strokeWidth="2" strokeDasharray="3,2" />
          <line x1="140" y1="35" x2="180" y2="30" stroke="#F7931E" strokeWidth="1" />
          <text x="184" y="34" fill="#F7931E" fontSize="10" fontWeight="700">HEAD</text>
        </g>
      )}
      {/* CHEST */}
      {showChest && (
        <g>
          <ellipse cx="110" cy="112" rx="34" ry="8" fill="none" stroke="#F7931E" strokeWidth="2" strokeDasharray="3,2" />
          <line x1="146" y1="112" x2="180" y2="108" stroke="#F7931E" strokeWidth="1" />
          <text x="184" y="112" fill="#F7931E" fontSize="10" fontWeight="700">CHEST</text>
        </g>
      )}
      {/* WAIST */}
      {showWaist && (
        <g>
          <ellipse cx="110" cy="158" rx="26" ry="6" fill="none" stroke="#F7931E" strokeWidth="2" strokeDasharray="3,2" />
          <line x1="138" y1="158" x2="180" y2="156" stroke="#F7931E" strokeWidth="1" />
          <text x="184" y="160" fill="#F7931E" fontSize="10" fontWeight="700">
            {has("body") ? "LENGTH" : "WAIST"}
          </text>
        </g>
      )}
      {/* HIP */}
      {showHip && (
        <g>
          <ellipse cx="110" cy="200" rx="32" ry="8" fill="none" stroke="#F7931E" strokeWidth="2" strokeDasharray="3,2" />
          <line x1="144" y1="200" x2="180" y2="200" stroke="#F7931E" strokeWidth="1" />
          <text x="184" y="204" fill="#F7931E" fontSize="10" fontWeight="700">HIP</text>
        </g>
      )}
      {/* CALF (both legs) */}
      {showCalf && (
        <g>
          <ellipse cx="94" cy="298" rx="12" ry="4" fill="none" stroke="#F7931E" strokeWidth="2" strokeDasharray="3,2" />
          <ellipse cx="126" cy="298" rx="12" ry="4" fill="none" stroke="#F7931E" strokeWidth="2" strokeDasharray="3,2" />
          <line x1="140" y1="298" x2="180" y2="296" stroke="#F7931E" strokeWidth="1" />
          <text x="184" y="301" fill="#F7931E" fontSize="10" fontWeight="700">CALF</text>
        </g>
      )}
    </svg>
  );
}

// Size charts keyed by product subcategory. Extend as needed.
// All measurements are in centimetres unless noted.
const CHARTS = {
  // Girls & Women — dress / skort / tights / vests share this chart
  "dress-girls-women": {
    title: "Dresses, Skorts, Tights & Vests — Girls & Women",
    columns: ["Size", "Chest (cm)", "Waist (cm)", "Hip (cm)"],
    rows: [
      ["5-6 Yrs",  "58-62", "53-56", "62-66"],
      ["7-8 Yrs",  "63-67", "57-60", "67-71"],
      ["9-10 Yrs", "68-72", "61-64", "72-77"],
      ["11-12 Yrs","73-78", "65-68", "78-83"],
      ["13-14 Yrs","79-83", "69-72", "84-89"],
      ["XS",       "80-84", "62-66", "86-90"],
      ["S",        "84-88", "66-70", "90-94"],
      ["M",        "88-92", "70-74", "94-98"],
      ["L",        "92-96", "74-78", "98-102"],
      ["XL",       "96-100","78-82", "102-106"],
    ],
  },
  // Boys & Men — hockey & gym shorts
  "shorts-boys-men": {
    title: "Mens / Boys Hockey & Gym Shorts",
    columns: ["Size", "Waist (cm)", "Hip (cm)"],
    rows: [
      ["7-8 Yrs",  "57-60", "67-71"],
      ["9-10 Yrs", "61-64", "72-77"],
      ["11-12 Yrs","65-68", "78-83"],
      ["13-14 Yrs","69-72", "84-89"],
      ["S",        "74-78", "88-92"],
      ["M",        "78-82", "92-96"],
      ["L",        "82-88", "96-102"],
      ["XL",       "88-94", "102-108"],
      ["2XL",      "94-100","108-114"],
    ],
  },
  // Shin skins — sleeve fit around calf
  "shin-skins": {
    title: "Shin Skins — Calf Sleeve",
    columns: ["Size", "Calf Circumference (cm)"],
    rows: [
      ["Small",  "28-32"],
      ["Medium", "33-37"],
      ["Large",  "38-42"],
    ],
  },
  // Rugby shorts
  "rugby-shorts": {
    title: "Rugby Shorts",
    columns: ["Size", "Waist (cm)", "Hip (cm)"],
    rows: [
      ["5-6 Yrs",  "53-56", "62-66"],
      ["7-8 Yrs",  "57-60", "67-71"],
      ["9-10 Yrs", "61-64", "72-77"],
      ["11-12 Yrs","65-68", "78-83"],
      ["13-14 Yrs","69-72", "84-89"],
      ["S",        "74-78", "88-92"],
      ["M",        "78-82", "92-96"],
      ["L",        "82-88", "96-102"],
      ["XL",       "88-94", "102-108"],
      ["2XL",      "94-100","108-114"],
    ],
  },
  // Rugby shirts
  "rugby-shirts": {
    title: "Rugby Shirts",
    columns: ["Size", "Chest (cm)", "Body Length (cm)"],
    rows: [
      ["7-8 Yrs",  "63-67", "48"],
      ["9-10 Yrs", "68-72", "52"],
      ["11-12 Yrs","73-78", "56"],
      ["13-14 Yrs","79-83", "60"],
      ["S",        "88-94", "68"],
      ["M",        "94-100","70"],
      ["L",        "100-106","72"],
      ["XL",       "106-112","74"],
      ["2XL",      "112-118","76"],
    ],
  },
  // Scrum caps — head circumference
  "scrum-caps": {
    title: "Scrum Caps — Head Circumference",
    columns: ["Size", "Head Circumference (cm)"],
    rows: [
      ["XS", "50-53 (Junior)"],
      ["S",  "54-56 (Youth)"],
      ["M",  "57-59 (Adult)"],
      ["L",  "60-62 (Adult)"],
    ],
  },
  // Shoulder pads
  "shoulder-pads": {
    title: "Rugby Shoulder Pads",
    columns: ["Size", "Chest (cm)"],
    rows: [
      ["2XS", "70-78"],
      ["XS",  "78-86"],
      ["S",   "86-92"],
      ["M",   "92-98"],
      ["L",   "98-104"],
      ["XL",  "104-110"],
      ["2XL", "110-118"],
    ],
  },
};

// Pick the right chart based on the product context.
function pickChartKey(product) {
  const cat = (product?.category || "").toLowerCase();
  const sub = (product?.subcategory || "").toLowerCase();
  const gen = (product?.gender || "").toLowerCase();

  if (cat === "scrum-caps") return "scrum-caps";
  if (cat === "rugby" && sub.includes("protection")) return "shoulder-pads";
  if (cat === "rugby" && sub.includes("shirt")) return "rugby-shirts";
  if (cat === "rugby") return "rugby-shorts";
  if (sub.includes("shin skin")) return "shin-skins";
  if (gen === "mens" || (cat === "hockey" && sub === "shorts")) return "shorts-boys-men";
  return "dress-girls-women";  // hockey/netball dresses/skorts/tights/vests
}

// Map a bare category string (used from the shop page without a specific product)
// to the best-fit chart key.
function chartKeyFromCategory(category) {
  const c = (category || "").toLowerCase();
  if (c === "scrum-caps") return "scrum-caps";
  if (c === "rugby") return "rugby-shorts";
  return "dress-girls-women"; // hockey / netball default
}

export default function SizeGuideModal({ product, open: controlledOpen, onOpenChange, category }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof controlledOpen === "boolean";
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    if (isControlled) onOpenChange?.(v);
    else setInternalOpen(v);
  };
  const [howOpen, setHowOpen] = useState(false);
  const chartKey = product ? pickChartKey(product) : chartKeyFromCategory(category);
  const chart = CHARTS[chartKey];
  const hints = measurementHintsFor(chartKey);

  return (
    <>
      {!isControlled && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="size-guide-trigger"
          className="inline-flex items-center gap-1.5 text-[#F7931E] text-xs font-bold uppercase tracking-widest hover:text-[#D97715] transition-colors"
        >
          <Ruler size={14} />
          Which size fits me?
        </button>
      )}

      {open && (
        <div
          data-testid="size-guide-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Size guide"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[#0A0A0A] border-2 border-[#F7931E]/40 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#262626] px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-[#F7931E] text-xs font-bold uppercase tracking-[0.2em]">Size Guide</p>
                <h3 className="text-white text-lg sm:text-xl font-black uppercase tracking-tight font-heading leading-tight">
                  {chart.title}
                </h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="size-guide-close"
                aria-label="Close size guide"
                className="w-9 h-9 flex items-center justify-center border border-[#262626] hover:border-[#F7931E] hover:text-[#F7931E] text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6">
              <p className="text-[#A3A3A3] text-sm mb-5 leading-relaxed">
                Measurements below are for the <em>body</em>, not the garment. Use a soft
                tape measure and record in centimetres. If you&apos;re between two sizes,
                size up for a relaxed fit and stay put for a compression fit.
              </p>

              <div className="overflow-x-auto border border-[#262626]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#171717]">
                      {chart.columns.map((c) => (
                        <th key={c} className="px-4 py-3 text-left text-[#F7931E] text-xs font-bold uppercase tracking-widest">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chart.rows.map((row, i) => (
                      <tr key={row[0]} className={i % 2 === 0 ? "bg-[#0A0A0A]" : "bg-[#111]"}>
                        {row.map((cell, j) => (
                          <td
                            key={j}
                            className={`px-4 py-3 ${j === 0 ? "text-white font-bold" : "text-[#D4D4D4]"}`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-5 border border-[#F7931E]/30 bg-[#F7931E]/5 p-4">
                <p className="text-white text-xs font-bold uppercase tracking-wider mb-1">
                  Still not sure?
                </p>
                <p className="text-[#A3A3A3] text-sm">
                  WhatsApp us on{" "}
                  <a
                    href="https://wa.me/27721960889"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#F7931E] hover:text-white transition-colors"
                  >
                    072 196 0889
                  </a>{" "}
                  with your measurements and we&apos;ll recommend a size for you.
                </p>
              </div>

              {/* Collapsible "How to measure" section */}
              <div className="mt-4 border border-[#262626]">
                <button
                  type="button"
                  onClick={() => setHowOpen((v) => !v)}
                  aria-expanded={howOpen}
                  data-testid="how-to-measure-toggle"
                  className="w-full flex items-center justify-between px-4 py-3 bg-[#171717] hover:bg-[#1f1f1f] transition-colors text-left"
                >
                  <span className="text-white text-sm font-bold uppercase tracking-wider">
                    How To Measure
                  </span>
                  <ChevronDown
                    size={18}
                    className={`text-[#F7931E] transition-transform ${howOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {howOpen && (
                  <div
                    data-testid="how-to-measure-content"
                    className="p-4 flex flex-col sm:flex-row gap-5 items-start bg-[#0A0A0A]"
                  >
                    <HowToMeasureDiagram points={hints} />
                    <ul className="flex-1 space-y-3">
                      {hints.map((h) => (
                        <li key={h.label} className="border-l-2 border-[#F7931E] pl-3">
                          <p className="text-[#F7931E] text-xs font-bold uppercase tracking-widest mb-0.5">
                            {h.label}
                          </p>
                          <p className="text-[#D4D4D4] text-sm leading-snug">{h.tip}</p>
                        </li>
                      ))}
                      <li className="text-[#A3A3A3] text-xs italic pt-1">
                        Best measured over light clothing, not over a padded jumper.
                        Wear the underwear you&apos;ll play in for the most accurate fit.
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
